import {
  Dictionary,
  SpeechTextBuilder,
  BuilderOptions,
} from './SpeechTextBuilder';
import {
  assign,
  filter,
  resolveURL,
  forEach,
  forEachIterator,
} from '../../../../common/src/ts/utils';
import { html as HTML_NS } from '../../../../common/src/ts/xmlns';
import { loadPLSFromURL } from './pls';
import { isDocument, isElement, iterateChildNodes } from '../../../../common/src/ts/utils/dom';
import { SpeechText } from './SpeechText';

async function loadDictionary(document: Document): Promise<Readonly<Dictionary>> {
  const links = document.querySelectorAll('link[rel=pronunciation]');
  const result:  Dictionary = {};
  const dictionaries = filter(links, link => link.namespaceURI === HTML_NS).map((link) => {
    if (document.location == null) {
      return {};
    }
    const url = resolveURL(link.getAttribute('href')!, document.location.href);
    return loadPLSFromURL(url);
  });
  for (const dictionary of dictionaries) {
    assign(result, await dictionary);
  }
  return result;
}

interface SpeechOptions extends BuilderOptions {
}

export async function documentToSpeechText(document: Document) {
  const range = document.createRange();
  range.setEnd(document, document.childNodes.length);
  return await rangeToSpeechText(range);
}

export async function rangeToSpeechText(range: Range) {
  const { startContainer } = range;
  const document = isDocument(startContainer) ? startContainer : startContainer.ownerDocument;
  if (document === null) {
    return;
  }

  const dictionary = await loadDictionary(document);
  const builder = new SpeechTextBuilder({ dictionary });
  builder.setRange(range);
  const text = builder.build();
  return text;
}

export async function speak(node: Node, options: Readonly<SpeechOptions> = {}) {
  if (node.ownerDocument === null) {
    return;
  }

  const fixedOptions: SpeechOptions = { ...options };
  if (fixedOptions.dictionary === undefined) {
    fixedOptions.dictionary = await loadDictionary(node.ownerDocument);
  }
  const builder = new SpeechTextBuilder(fixedOptions);
  const range = node.ownerDocument.createRange();
  range.selectNode(node);
  builder.setRange(range);
  const text = builder.build();
  text.speak();
}

const CLASS_TTS_SPEAKING = 'ark-tts-speaking';
const CLASS_TTS_ACTIVE = 'ark-tts-active';
const CLASS_TTS_SPOKEN = 'ark-tts-spoken';

export function setSpeakingClass(node: Node, range: Range) {
  if (isElement(node)) {
    node.classList.add(CLASS_TTS_SPEAKING);
  }
  forEachIterator(iterateChildNodes(node, range), node => setSpeakingClass(node, range));
}

export function setActiveClass(node: Node, range: Range, nodes: Node[]) {
  if (isElement(node)) {
    if (nodes.indexOf(node) >= 0) {
      node.classList.remove(CLASS_TTS_SPOKEN);
      node.classList.add(CLASS_TTS_ACTIVE);
    } else {
      node.classList.remove(CLASS_TTS_ACTIVE);
      node.classList.add(CLASS_TTS_SPOKEN);
    }
  }
  forEachIterator(iterateChildNodes(node, range), node => setActiveClass(node, range, nodes));
}

export function clearFeedbackClass(doc: Document) {
  forEach(doc.querySelectorAll(`.${CLASS_TTS_SPEAKING}`), (elem) => {
    elem.classList.remove(CLASS_TTS_SPEAKING);
    elem.classList.remove(CLASS_TTS_SPOKEN);
    elem.classList.remove(CLASS_TTS_ACTIVE);
  });
}

export async function cancelIfSpeaking(text?: SpeechText) {
  if (text !== undefined) {
    await new Promise((resolve) => {
      speechSynthesis.cancel();
      const onend = text.onend;
      text.onend = (info) => {
        if (onend !== null) {
          onend.call(text, info);
        }
        // このタイミングで次の再生をするとなぜか止まってしまうバグがあるので500ms遅延させる
        setTimeout(resolve, 500);
      };
    });
  } else if (speechSynthesis.speaking || speechSynthesis.paused) {
    speechSynthesis.cancel();
    await new Promise(resolve => setTimeout(resolve, 500));
  }
}
